<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-4">
      <v-btn
        v-if="isAdmin"
        style="margin-top: -2.25rem"
        @click="openUpsertDialog()"
        color="primary"
        class="mb-8 mr-2">
        {{ $t('entities.products.createGlobalProduct') }}
      </v-btn>
      <v-spacer />
      <CatalogSwitch
        :route="{ name: 'navigationDrawer.shopV2.products' }"
        :initial-value="$t('entities.products.globalProducts')"
        :options="[
          $t('entities.products.titleV2'),
          $t('entities.products.globalProducts'),
        ]" />
    </div>
    <v-card>
      <v-row class="px-4 pt-4 mb-4" align="center">
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="pagination.size"
            :items="rowsPerPageItems"
            :menu-props="{ bottom: true, offsetY: true }"
            :no-data-text="$t('components.noData.noEntriesFound')"
            :disabled="!rowsPerPageItems.length || areProductsLoading"
            hide-details="auto"
            solo>
            <template v-slot:label>
              {{ $t('components.rowsPerPage.entries') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('components.rowsPerPage.entries') }}: {{ data.item }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="sort"
            :items="sortOptions"
            :disabled="areProductsLoading"
            solo
            hide-details="auto">
            <template v-slot:label>
              {{ $t('miscellaneous.sortBy') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('miscellaneous.sortBy') }}: {{ data.item?.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="selectedCategory"
            :items="categoryOptions"
            :disabled="areProductsLoading || areCategoriesLoading"
            return-object
            clearable
            solo
            hide-details="auto">
            <template v-slot:label>
              {{ $t('entities.categories.entity') }}
            </template>
            <template v-slot:selection="data">
              {{ $t('entities.categories.entity') }}: {{ data.item?.text }}
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="nameFilter"
            :placeholder="searchFieldPlaceholder"
            :disabled="areProductsLoading"
            prepend-inner-icon="mdi-magnify"
            solo
            clearable
            hide-details />
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="products"
        :loading="areProductsLoading"
        :items-per-page="pagination.size"
        item-key="id"
        class="elevation-0 px-2"
        hide-default-footer>
        <template v-slot:item.image="{ item }">
          <img :src="item.image" width="40" />
        </template>
        <template v-slot:item.category="{ item }">
          <span>{{ getCategoryName(item.category_id) }}</span>
        </template>
        <template v-slot:item.variantsCount="{ item }">
          <span>{{ item.variants?.length ?? 0 }}</span>
        </template>
        <template v-if="isAdmin || isManager" v-slot:item.actions="{ item }">
          <v-tooltip v-if="isAdmin || isManager" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                depressed
                x-small
                dark
                fab
                color="success"
                class="mr-2"
                :disabled="!arenaId"
                v-bind="attrs"
                v-on="on"
                @click="openAddToArenaDialog(item)">
                <v-icon small>mdi-source-pull</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t('entities.products.sendGlobalProductToArenaProducts') }}
            </span>
          </v-tooltip>
          <template v-if="isAdmin">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  depressed
                  x-small
                  dark
                  fab
                  color="error"
                  class="mr-2"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDeleteDialog(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('entities.products.deleteEntity') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  depressed
                  x-small
                  dark
                  fab
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="openUpsertDialog(item)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('entities.products.editEntity') }}</span>
            </v-tooltip>
          </template>
        </template>
      </v-data-table>
      <v-layout
        v-if="pagination.count"
        class="text-center mt-3 pb-4"
        justify-center>
        <v-flex>
          <v-pagination
            v-model="pagination.page"
            :length="pagination.last_page"
            :total-visible="$vuetify.breakpoint.mdAndUp ? 10 : 5"
            :disabled="areProductsLoading"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right" />
          <h5 class="card-title font-weight-light mt-2">
            {{
              `${pagination.from} - ${pagination.to} ${$t(
                'components.pagination.of',
              )} ${pagination.count}`
            }}
          </h5>
        </v-flex>
      </v-layout>
    </v-card>
    <UpsertDialog
      v-model="isUpsertDialogOpen"
      :categories="categories"
      :is-loading="isUpsertSubmitting"
      :product="productToUpsert"
      @product:upsert="saveProduct" />
    <DeleteDialog
      v-model="isDeleteDialogOpen"
      :loading="isDeleteSubmitting"
      :title="$t('entities.products.deleteEntity')"
      @cancel="closeDeleteDialog"
      @confirm="confirmDelete">
      <p class="mb-0">
        {{ $t('miscellaneous.delete') }}
        {{ productToDelete?.name || $t('entities.products.entity') }}?
      </p>
    </DeleteDialog>
    <ArenaUpsertDialog
      v-model="isAddToArenaDialogOpen"
      :arena-id="arenaId"
      :categories="categories"
      :product="productToAddToArena"
      :is-submitting="isAddToArenaSubmitting"
      :taxes="arenaTaxes"
      @product:upsert="addProductToArena" />
  </div>
</template>

<script>
import CatalogSwitch from '@/components/CatalogSwitch.vue'
import CategoriesApi from '@/services/shop/CategoriesApiV2'
import GlobalProduct from '@/models/shop/GlobalProduct'
import ArenaProduct from '@/models/shop/ArenaProduct'
import TaxJurisdiction from '@/models/setup/TaxJurisdiction'
import { HEADERS } from './tableHeaders'
import UpsertDialog from './UpsertDialog.vue'
import ArenaUpsertDialog from '../arena-products/UpsertDialog.vue'
import DeleteDialog from '../arena-products/DeleteDialog.vue'
import isRole from '@/mixins/crud/computed/isRole'
import { mapActions } from 'vuex'

const defaultPagination = () => ({
  size: 10,
  from: 0,
  to: 0,
  page: 1,
  last_page: 0,
  count: 0,
})

export default {
  name: 'GlobalProducts',
  components: {
    CatalogSwitch,
    UpsertDialog,
    ArenaUpsertDialog,
    DeleteDialog,
  },
  mixins: [isRole],
  data() {
    return {
      areCategoriesLoading: false,
      areProductsLoading: false,
      categories: [],
      isDeleteDialogOpen: false,
      isDeleteSubmitting: false,
      isUpsertDialogOpen: false,
      isUpsertSubmitting: false,
      isAddToArenaDialogOpen: false,
      isAddToArenaSubmitting: false,
      productToAddToArena: null,
      existingArenaProductId: null,
      taxJurisdictionForArena: null,
      pagination: defaultPagination(),
      selectedCategory: null,
      sort: '-created_at',
      productToDelete: null,
      products: [],
      productToUpsert: {},
      nameFilter: '',
      nameDebounce: null,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    arenaTaxes() {
      return this.taxJurisdictionForArena?.taxes || []
    },
    headers() {
      const shouldShowActions = this.isAdmin || this.isManager
      return HEADERS.filter(
        (header) => shouldShowActions || header.value !== 'actions',
      ).map((header) => ({
        ...header,
        text: this.$t(header.textKey),
      }))
    },
    rowsPerPageItems() {
      return [5, 10, 25, 50]
    },
    categoryOptions() {
      const categoryItems = Array.isArray(this.categories)
        ? this.categories.map((category) => ({
            text: category.name,
            value: category.code,
          }))
        : []
      return [
        { text: this.$t('components.rowsPerPage.all'), value: null },
        ...categoryItems,
      ]
    },
    searchFieldPlaceholder() {
      const searchBy = this.$t('components.dataTableHeader.searchBy')
      const name = this.$t('generalAttributes.name')
      return `${searchBy} ${name}`
    },
    sortOptions() {
      return [
        {
          text: this.$t('entities.products.filters.newest'),
          value: '-created_at',
        },
        {
          text: this.$t('entities.products.filters.oldest'),
          value: 'created_at',
        },
        {
          text: this.$t('entities.products.filters.nameAsc'),
          value: 'name',
        },
        {
          text: this.$t('entities.products.filters.nameDesc'),
          value: '-name',
        },
        {
          text: this.$t('entities.products.filters.categoryAsc'),
          value: 'category',
        },
        {
          text: this.$t('entities.products.filters.categoryDesc'),
          value: '-category',
        },
      ]
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    async fetchCategories() {
      this.areCategoriesLoading = true
      try {
        const response = await CategoriesApi.get()
        this.categories = response.data?.data || []
      } catch (error) {
        console.error(error)
        this.categories = []
      } finally {
        this.areCategoriesLoading = false
      }
    },
    async fetchProducts() {
      this.areProductsLoading = true
      try {
        const { page, size } = this.pagination
        const params = { page, size, sort: this.sort }
        if (this.selectedCategory?.value) {
          params.category_code = this.selectedCategory.value
        }
        if (this.nameFilter?.trim()) {
          params.name = this.nameFilter.trim()
        }
        const response = await GlobalProduct.api.list(params)
        const payload = response.data?.data || []
        this.products = payload.map((dto) => new GlobalProduct(dto))
        this.setPaginationTotals(response.data?.metadata, this.products.length)
      } catch (error) {
        console.error(error)
        this.products = []
        this.setPaginationTotals()
      } finally {
        this.areProductsLoading = false
      }
    },
    getCategoryName(categoryId) {
      const category = this.categories.find((it) => it.id === categoryId)
      return category?.name ?? '-'
    },
    openUpsertDialog(product = {}) {
      this.productToUpsert = product
      this.isUpsertDialogOpen = true
    },
    getCategoryCodeById(categoryId) {
      const category = (this.categories || []).find(
        (c) => c.id === categoryId || c.id === String(categoryId),
      )
      return category?.code ?? null
    },
    async fetchArenaProductsByCategory(arenaId, categoryCode) {
      try {
        const response = await ArenaProduct.api.list(arenaId, {
          page: 1,
          size: 100,
          category_code: categoryCode,
        })
        const data = response.data?.data
        return Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Failed to fetch arena products by category', error)
        return []
      }
    },
    findArenaProductByGlobalProductId(arenaProducts, globalProductId) {
      if (!arenaProducts?.length) return null
      return arenaProducts.find((p) => p.global_product_id === globalProductId)
    },
    mergeMissingVariantsIntoArenaProduct(globalProduct, existingArenaProduct) {
      const globalVariants = globalProduct.variants || []
      const existingVariants = existingArenaProduct?.variants || []
      const arenaVariantGlobalIds = new Set(
        existingVariants
          .map((v) => v.global_variant_id || v.globalVariantId)
          .filter(Boolean),
      )
      const existingForDialog = existingVariants.map((v) => ({
        id: v.id,
        name: v.name || '',
        description: v.description || '',
        sku: v.sku || '',
        image: v.image || '',
        attributes: v.attributes || {},
        pack_type: v.pack_type || null,
        active: !!v.active,
        sort_order: v.sort_order ?? 1,
        global_variant_id: v.global_variant_id || v.globalVariantId || '',
        prices: Array.isArray(v.prices)
          ? v.prices.map((p) => ({
              id: p.id,
              gross_price: p.gross_price ?? '',
              currency_id: p.currency_id || '',
            }))
          : [],
        stock: {
          available: v.stock?.available ?? null,
          waste: v.stock?.waste ?? null,
        },
        eligibleForCreation: true,
      }))
      const missingFromGlobal = globalVariants.filter(
        (gv) =>
          !arenaVariantGlobalIds.has(String(gv.id)) &&
          !arenaVariantGlobalIds.has(gv.id),
      )
      const missingForDialog = missingFromGlobal.map((v) => ({
        name: v.name || '',
        description: v.description || '',
        sku: v.sku || '',
        image: v.image || '',
        attributes: v.attributes || {},
        pack_type: v.pack_type || null,
        active: false,
        sort_order: v.sort_order || 1,
        global_variant_id: v.id || '',
        prices: [],
        stock: { available: null, waste: null },
        eligibleForCreation: false,
      }))
      const mergedVariants = [...existingForDialog, ...missingForDialog]
      const base = existingArenaProduct
        ? {
            category_id: existingArenaProduct.category_id,
            name: existingArenaProduct.name || '',
            description: existingArenaProduct.description || '',
            image: existingArenaProduct.image || '',
            sort_order: existingArenaProduct.sort_order ?? 1,
            track_stock: !!existingArenaProduct.track_stock,
            global_product_id:
              existingArenaProduct.global_product_id || globalProduct.id || '',
            taxes: existingArenaProduct.taxes,
          }
        : {
            category_id: globalProduct.category_id,
            name: globalProduct.name || '',
            description: globalProduct.description || '',
            image: globalProduct.image || '',
            sort_order: globalProduct.sort_order || 1,
            track_stock: !!globalProduct.track_stock,
            global_product_id: globalProduct.id || '',
            taxes: [],
          }
      return {
        ...base,
        id: undefined,
        variants: mergedVariants,
      }
    },
    async openAddToArenaDialog(product) {
      if (!product || !this.arenaId) return
      this.existingArenaProductId = null
      const categoryCode = this.getCategoryCodeById(product.category_id)
      const arenaProducts = await this.fetchArenaProductsByCategory(
        this.arenaId,
        categoryCode,
      )
      const existingArenaProduct = this.findArenaProductByGlobalProductId(
        arenaProducts,
        product.id,
      )
      this.productToAddToArena = this.mergeMissingVariantsIntoArenaProduct(
        product,
        existingArenaProduct,
      )
      if (existingArenaProduct?.id) {
        this.existingArenaProductId = existingArenaProduct.id
      }
      await this.fetchTaxJurisdictionForArena()
      this.$nextTick(() => {
        this.isAddToArenaDialogOpen = true
      })
    },
    mapGlobalProductToArenaFormat(globalProduct) {
      return {
        category_id: globalProduct.category_id,
        name: globalProduct.name || '',
        description: globalProduct.description || '',
        image: globalProduct.image || '',
        sort_order: globalProduct.sort_order || 1,
        track_stock: !!globalProduct.track_stock,
        global_product_id: globalProduct.id || '',
        taxes: [],
        variants: (globalProduct.variants || []).map((variant) => ({
          name: variant.name || '',
          description: variant.description || '',
          sku: variant.sku || '',
          image: variant.image || '',
          attributes: variant.attributes || {},
          pack_type: variant.pack_type || null,
          active: false,
          sort_order: variant.sort_order || 1,
          global_variant_id: variant.id || '',
          prices: [],
          stock: {
            available: null,
            waste: null,
          },
        })),
      }
    },
    async fetchTaxJurisdictionForArena() {
      if (!this.arenaId) {
        this.taxJurisdictionForArena = null
        return
      }
      try {
        const { api } = TaxJurisdiction
        const { data } = await api.getArenaTaxJurisdiction(this.arenaId)
        this.taxJurisdictionForArena = new TaxJurisdiction(data)
      } catch (error) {
        console.error(error)
        this.taxJurisdictionForArena = null
      }
    },
    async addProductToArena(payload) {
      if (!this.arenaId) return
      this.isAddToArenaSubmitting = true
      const { variants = [], ...productPayload } = payload || {}
      const variantsToCreate = variants?.length
        ? variants.filter((v) => v && !v.id && v.global_variant_id)
        : []
      let productId
      try {
        if (this.existingArenaProductId) {
          productId = this.existingArenaProductId
          const patchPayload = this.buildArenaProductPatchPayload(
            productPayload,
            this.productToAddToArena,
          )
          if (Object.keys(patchPayload).length > 1) {
            await ArenaProduct.api.update(
              this.arenaId,
              this.existingArenaProductId,
              patchPayload,
            )
          }
          if (variantsToCreate.length) {
            await this.createArenaVariantsFromGlobal(
              productId,
              variantsToCreate,
            )
          }
        } else {
          const response = await ArenaProduct.api.create(
            this.arenaId,
            productPayload,
          )
          productId = response.data?.id
          if (productId && variantsToCreate.length) {
            await this.createArenaVariantsFromGlobal(
              productId,
              variantsToCreate,
            )
          }
        }
        this.isAddToArenaDialogOpen = false
        this.productToAddToArena = null
        this.existingArenaProductId = null
        this.showSnackbar({
          color: 'success',
          message: this.$t('entities.products.addedToArenaSuccess'),
        })
      } catch (error) {
        console.error(error)
      } finally {
        this.isAddToArenaSubmitting = false
      }
    },
    buildArenaProductPatchPayload(formPayload, originalProduct) {
      if (!originalProduct) return { id: this.existingArenaProductId }
      const payload = { id: this.existingArenaProductId }
      const nameTrimmed = (formPayload.name || '').trim()
      const nameOrig = (originalProduct.name || '').trim()
      if (nameTrimmed !== nameOrig) payload.name = nameTrimmed
      const descForm = formPayload.description || ''
      const descOrig = originalProduct.description || ''
      if (descForm !== descOrig) payload.description = descForm
      if (formPayload.category_id !== originalProduct.category_id) {
        payload.category_id = formPayload.category_id
      }
      const origTaxIds = (originalProduct.taxes || [])
        .map((t) => (typeof t === 'object' ? t.id : t))
        .filter((id) => id != null)
        .sort()
      const formTaxIds = (formPayload.taxes || []).slice().sort()
      const taxesEqual =
        origTaxIds.length === formTaxIds.length &&
        origTaxIds.every((id, i) => id === formTaxIds[i])
      if (!taxesEqual) payload.taxes = formPayload.taxes
      const imageForm = formPayload.image || ''
      const imageOrig = originalProduct.image || ''
      if (imageForm !== imageOrig) payload.image = imageForm
      const trackForm = !!formPayload.track_stock
      const trackOrig = !!originalProduct.track_stock
      if (trackForm !== trackOrig) payload.track_stock = trackForm
      return payload
    },
    async createArenaVariantsFromGlobal(productId, variants) {
      if (!this.arenaId || !productId) return
      for (const variant of variants) {
        if (!variant) continue
        try {
          const { id, prices = [], ...variantPayload } = variant
          if (id) {
            const hasVariantChanges =
              Object.keys(variantPayload).length > 0 || prices.length > 0
            if (hasVariantChanges) {
              await ArenaProduct.api.updateArenaVariant(
                this.arenaId,
                productId,
                id,
                variantPayload,
              )
              await this.updateArenaVariantPrices(productId, id, prices)
            }
          } else {
            await ArenaProduct.api.createArenaVariant(this.arenaId, productId, {
              ...variantPayload,
              prices,
            })
          }
        } catch (error) {
          console.error('Failed to create arena variant', error)
        }
      }
    },
    async updateArenaVariantPrices(productId, variantId, prices = []) {
      const priceUpdates = []
      prices.forEach((price) => {
        if (!price || !price.gross_price) return
        const grossPrice = Number(price.gross_price)
        if (Number.isNaN(grossPrice)) return
        const payload = {
          gross_price: grossPrice,
          currency_id: price.currency_id,
        }
        if (price.id) {
          priceUpdates.push(
            ArenaProduct.api.updateArenaVariantPrice(
              this.arenaId,
              productId,
              variantId,
              price.id,
              payload,
            ),
          )
        } else {
          priceUpdates.push(
            ArenaProduct.api.createArenaVariantPrice(
              this.arenaId,
              productId,
              variantId,
              payload,
            ),
          )
        }
      })
      if (priceUpdates.length) {
        await Promise.all(priceUpdates)
      }
    },
    openDeleteDialog(product) {
      this.productToDelete = product
      this.isDeleteDialogOpen = true
    },
    closeDeleteDialog() {
      this.isDeleteDialogOpen = false
      this.productToDelete = null
    },
    async saveProduct({ id, updates, variants = [], deletedVariantIds = [] }) {
      this.isUpsertSubmitting = true
      let productId = id
      try {
        if (id) {
          const response = await GlobalProduct.api.update(id, updates)
          productId = response.data?.id || response.data?.data?.id || id
        } else {
          const response = await GlobalProduct.api.create(updates)
          productId = response.data?.id || response.data?.data?.id
        }
        if (productId && Array.isArray(variants) && variants.length) {
          await this.upsertVariants(productId, variants)
        }
        if (productId && deletedVariantIds.length) {
          await this.deleteVariants(productId, deletedVariantIds)
        }
        this.isUpsertDialogOpen = false
        await this.fetchProducts()
      } catch (error) {
        console.error(error)
      } finally {
        this.isUpsertSubmitting = false
      }
    },
    async deleteVariants(productId, ids) {
      for (const variantId of ids) {
        try {
          await GlobalProduct.api.deleteVariant(productId, variantId)
        } catch (error) {
          console.error('Failed to delete global variant', variantId, error)
        }
      }
    },
    async upsertVariants(productId, variants) {
      for (const variant of variants) {
        if (!variant) continue
        try {
          const { id, ...payload } = variant
          if (id) {
            await GlobalProduct.api.updateVariant(productId, id, payload)
          } else {
            await GlobalProduct.api.createVariant(productId, payload)
          }
        } catch (error) {
          console.error('Failed to upsert global variant', error)
        }
      }
    },
    async confirmDelete() {
      if (!this.productToDelete?.id) {
        this.closeDeleteDialog()
        return
      }
      this.isDeleteSubmitting = true
      try {
        await GlobalProduct.api.delete(this.productToDelete.id)
        this.closeDeleteDialog()
        await this.fetchProducts()
      } catch (error) {
        console.error(error)
      } finally {
        this.isDeleteSubmitting = false
      }
    },
    setPaginationTotals(metadata = null, dataLength = 0) {
      const currentPage = this.pagination.page || 1
      const size = this.pagination.size || 10
      const count = metadata?.count ?? dataLength ?? 0
      const lastPage = size ? Math.ceil((count || 0) / size) : 0
      const from = count ? (currentPage - 1) * size + 1 : 0
      const to = count ? Math.min(from + size - 1, count) : 0
      this.pagination.from = from
      this.pagination.to = to
      this.pagination.page = currentPage
      this.pagination.last_page = lastPage
      this.pagination.count = count
    },
    onFirstPage() {
      return this.pagination.page === 1
    },
    setPageToOne() {
      this.pagination.page = 1
    },
  },
  watch: {
    selectedCategory(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchProducts()
      } else {
        this.setPageToOne()
      }
    },
    sort(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchProducts()
      } else {
        this.setPageToOne()
      }
    },
    nameFilter() {
      if (this.nameDebounce) {
        clearTimeout(this.nameDebounce)
      }
      this.nameDebounce = setTimeout(() => {
        if (this.onFirstPage()) {
          this.fetchProducts()
        } else {
          this.setPageToOne()
        }
      }, 500)
    },
    'pagination.size'(newVal, oldVal) {
      if (newVal === oldVal) return
      if (this.onFirstPage()) {
        this.fetchProducts()
      } else {
        this.setPageToOne()
      }
    },
    'pagination.page'(newVal, oldVal) {
      if (newVal === oldVal) return
      this.fetchProducts()
    },
    isAddToArenaDialogOpen(value) {
      if (!value) {
        this.productToAddToArena = null
        this.existingArenaProductId = null
      }
    },
  },
  async created() {
    await Promise.all([
      this.fetchCategories(),
      this.fetchProducts(),
      this.fetchTaxJurisdictionForArena(),
    ])
  },
  beforeDestroy() {
    this.nameDebounce && clearTimeout(this.nameDebounce)
  },
}
</script>
